<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/button';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {Button} from '@react-spectrum/button';
import Bell from '@spectrum-icons/workflow/Bell';
```

---
category: Buttons
---

# Button

<p>{docs.exports.Button.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Button']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/button/'}
  ]} />

## Example

```tsx example
<Button variant="cta">Test</Button>
```

## Content

Buttons can have a label, and icon, or both. An icon is provided by passing an icon component to the `icon` prop.
A visible label can be provided by passing children.

```tsx example
import {Text} from '@react-spectrum/typography';

<Button variant="primary">Label only</Button>
<Button variant="primary">
  <Bell />
  <Text>Icon + Label</Text>
</Button>
<Button variant="primary" aria-label="Icon only">
  <Bell />
</Button>
```

### Accessibility

If no visible label is provided (e.g. an icon only button),
an alternative text label must be provided to identify the control for accessibility. This should be added using
the `aria-label` prop.

### Internationalization

In order to internationalize a button, a localized string should be passed to the `children` or `aria-label` prop.

## Events

Buttons support user interactions via mouse, keyboard, and touch. You can handle all of these via the `onPress`
prop.

The following example uses an `onPress` handler to update a counter stored in React state.

```tsx example
function Example() {
  let [count, setCount] = React.useState(0);

  return (
    <Button variant="primary" onPress={() => setCount(c => c + 1)}>{count} Dogs</Button>
  );
}
```

## Props

<PropTable component={docs.exports.Button} links={docs.links} />

## Visual options

### Call to action
[View guidelines](https://spectrum.adobe.com/page/button/#Call-to-action)

```tsx example
<Button variant="cta">Test</Button>
```

### Primary
[View guidelines](https://spectrum.adobe.com/page/button/#Primary)

```tsx example
<Button variant="primary">Test</Button>
<Button variant="primary" isQuiet>Test</Button>
```

### Secondary
[View guidelines](https://spectrum.adobe.com/page/button/#Secondary)

```tsx example
<Button variant="secondary">Test</Button>
<Button variant="secondary" isQuiet>Test</Button>
```

### Over background
[View guidelines](https://spectrum.adobe.com/page/button/#Over-background)

```tsx example
<div style={{backgroundColor: 'rgb(15, 121, 125)', padding: '15px 20px'}}>
  <Button variant="overBackground">Test</Button>
  <Button variant="overBackground" isQuiet>Test</Button>
</div>
```

### Negative
[View guidelines](https://spectrum.adobe.com/page/button/#Negative)

```tsx example
<Button variant="negative">Test</Button>
<Button variant="negative" isQuiet>Test</Button>
```
